import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Flame, Heart, Activity, Trash2, Save, ChevronRight, Clock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";

const SEV_COLOR = { critical: "text-rose-300 border-rose-500/40 bg-rose-500/10", hot: "text-amber-300 border-amber-500/40 bg-amber-500/10" };

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtClock(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" }); }
  catch { return ""; }
}

function EventDetail({ eventId, onClose, onDeleted }) {
  const [ev, setEv] = useState(null);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    setEv(null);
    api.get(`/hot-pitcher-events/${eventId}`).then((r) => {
      setEv(r.data);
      setLabel(r.data.label || "");
      setNotes(r.data.notes || "");
    });
  }, [eventId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/hot-pitcher-events/${eventId}`, { label, notes });
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this replay event?")) return;
    try {
      await api.delete(`/hot-pitcher-events/${eventId}`);
      toast.success("Deleted");
      onDeleted?.();
    } catch (e) { toast.error("Could not delete"); }
  };

  if (!ev) return <div className="text-white/40 text-sm p-4">Loading…</div>;

  const data = (ev.chart || []).map((p) => ({
    time: fmtClock(p.ts),
    ts: p.ts,
    hr: p.hr,
    hrv: p.hrv,
  }));

  return (
    <div className="space-y-4" data-testid="replay-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Link to={`/dashboard/player/${ev.player_user_id}`} className="text-xl font-display font-bold hover:underline">
              {ev.player_name}
            </Link>
            <Badge className={SEV_COLOR[ev.peak_severity] || ""}>{ev.peak_severity?.toUpperCase()}</Badge>
            {ev.ended_at == null && <Badge variant="outline" className="text-emerald-300 border-emerald-500/40">LIVE</Badge>}
          </div>
          <div className="text-xs text-white/50 mt-1">
            Started {fmtTime(ev.started_at)} {ev.ended_at && `→ ended ${fmtTime(ev.ended_at)}`} · {ev.duration_min != null ? `${ev.duration_min} min` : "in progress"}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="replay-close">Close</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <Stat label="Peak HR" value={ev.peak_hr} unit="bpm" color="text-rose-300" />
        <Stat label="ΔHR vs baseline" value={ev.peak_hr_delta != null ? `+${ev.peak_hr_delta}` : "—"} unit={`vs ${ev.baseline_rhr ?? "—"}`} color="text-rose-200" />
        <Stat label="Min HRV" value={ev.min_hrv ?? "—"} unit="ms" color="text-cyan-300" />
        <Stat label="Max HRV drop" value={ev.max_hrv_drop_pct != null ? `${Math.round(ev.max_hrv_drop_pct * 100)}%` : "—"} unit={`vs ${ev.baseline_hrv ?? "—"}`} color="text-cyan-200" />
      </div>

      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="time" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis yAxisId="hr" tick={{ fill: "rgba(244,114,182,0.7)", fontSize: 10 }} />
            <YAxis yAxisId="hrv" orientation="right" tick={{ fill: "rgba(34,211,238,0.7)", fontSize: 10 }} />
            <Tooltip
              contentStyle={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
            />
            {ev.baseline_rhr && (
              <ReferenceLine yAxisId="hr" y={ev.baseline_rhr} stroke="rgba(244,114,182,0.4)" strokeDasharray="4 4" label={{ value: "RHR baseline", fill: "rgba(244,114,182,0.6)", fontSize: 10, position: "insideLeft" }} />
            )}
            <Line yAxisId="hr" type="monotone" dataKey="hr" stroke="#f43f5e" strokeWidth={2} dot={false} name="HR" connectNulls />
            <Line yAxisId="hrv" type="monotone" dataKey="hrv" stroke="#22d3ee" strokeWidth={2} dot={false} name="HRV" connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs">Label</Label>
          <Input
            value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder='e.g. "7th-inning shutdown" or "weighted-ball PR"'
            data-testid="replay-label-input"
          />
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea
            value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="What did you see? Context, pitch count, weather, etc."
            data-testid="replay-notes-input"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={del} data-testid="replay-delete">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
          </Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="replay-save">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Save annotations
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, unit, color = "text-white" }) {
  return (
    <div className="rounded border border-white/10 bg-white/5 p-2">
      <div className="text-[10px] tracking-[0.18em] uppercase text-white/40">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className={`font-mono-num font-bold text-lg ${color}`}>{value}</span>
        {unit && <span className="text-[10px] text-white/40">{unit}</span>}
      </div>
    </div>
  );
}

export default function CoachReplays() {
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [filters, setFilters] = useState({ days: "30", player_user_id: "", min_peak_hr: "", severity: "", only_closed: false });
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (filters.days) params.set("days", filters.days);
    if (filters.player_user_id) params.set("player_user_id", filters.player_user_id);
    if (filters.min_peak_hr) params.set("min_peak_hr", filters.min_peak_hr);
    if (filters.severity) params.set("severity", filters.severity);
    if (filters.only_closed) params.set("only_closed", "true");
    const r = await api.get(`/hot-pitcher-events?${params.toString()}`);
    setEvents(r.data?.events || []);
  };

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="replays-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight flex items-center gap-2">
          <Flame className="h-7 w-7 text-rose-400" /> Hot-Pitcher Replay
        </h1>
        <p className="text-white/60 mt-1 text-sm md:text-base">
          Every time a pitcher's in-game biometrics enter the hot/critical zone, ArmIQ snapshots the HR+HRV trace. Review last night's bullpen, annotate, and feed it back into your pitch-count rules.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>{events.length} event{events.length === 1 ? "" : "s"} match</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Window</Label>
            <Select value={filters.days} onValueChange={(v) => setFilters({ ...filters, days: v })}>
              <SelectTrigger data-testid="filter-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24h</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Athlete</Label>
            <Select value={filters.player_user_id || "all"} onValueChange={(v) => setFilters({ ...filters, player_user_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-player"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All athletes</SelectItem>
                {players.map((p) => (
                  <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Peak severity</Label>
            <Select value={filters.severity || "any"} onValueChange={(v) => setFilters({ ...filters, severity: v === "any" ? "" : v })}>
              <SelectTrigger data-testid="filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="hot">Hot+</SelectItem>
                <SelectItem value="critical">Critical only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Min peak HR</Label>
            <Input type="number" min={80} max={220} value={filters.min_peak_hr}
              onChange={(e) => setFilters({ ...filters, min_peak_hr: e.target.value })}
              placeholder="e.g. 160" data-testid="filter-min-hr" />
          </div>
          <div className="flex items-end">
            <label className="text-xs flex items-center gap-2 text-white/70 cursor-pointer">
              <input type="checkbox" checked={filters.only_closed}
                onChange={(e) => setFilters({ ...filters, only_closed: e.target.checked })}
                data-testid="filter-only-closed" />
              Hide live events
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2" data-testid="replay-list">
          {events.length === 0 && (
            <Card><CardContent className="py-8 text-center text-white/50">No replay events in this window.</CardContent></Card>
          )}
          {events.map((ev) => {
            const isActive = ev.event_id === activeId;
            return (
              <button
                key={ev.event_id}
                onClick={() => setActiveId(ev.event_id)}
                className={`w-full text-left rounded-lg border p-3 transition ${isActive ? "border-rose-400/60 bg-rose-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                data-testid={`replay-row-${ev.event_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{ev.player_name}</span>
                    <Badge className={SEV_COLOR[ev.peak_severity] || ""}>{ev.peak_severity?.toUpperCase()}</Badge>
                    {ev.ended_at == null && <Badge variant="outline" className="text-emerald-300 border-emerald-500/40">LIVE</Badge>}
                  </div>
                  <ChevronRight className="h-4 w-4 text-white/40" />
                </div>
                <div className="text-xs text-white/50 mt-1 flex items-center gap-3 flex-wrap font-mono-num">
                  <span><Heart className="h-3 w-3 inline mr-1 text-rose-300" />{ev.peak_hr} bpm{ev.peak_hr_delta != null ? <span className="text-white/40 ml-1">(+{ev.peak_hr_delta})</span> : null}</span>
                  {ev.min_hrv != null && <span><Activity className="h-3 w-3 inline mr-1 text-cyan-300" />HRV {ev.min_hrv}</span>}
                  {ev.duration_min != null && <span><Clock className="h-3 w-3 inline mr-1" />{ev.duration_min}m</span>}
                </div>
                <div className="text-[10px] text-white/40 mt-1">{fmtTime(ev.started_at)}</div>
                {ev.label && <div className="text-xs text-white/70 italic mt-1">"{ev.label}"</div>}
              </button>
            );
          })}
        </div>

        <div className="md:sticky md:top-4 self-start">
          <Card>
            <CardContent className="pt-6">
              {activeId ? (
                <EventDetail eventId={activeId} onClose={() => setActiveId(null)} onDeleted={() => { setActiveId(null); load(); }} />
              ) : (
                <div className="text-center text-white/50 py-12 text-sm">
                  Pick an event on the left to view the HR/HRV replay and annotate it.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
